import { gsap } from 'gsap';

/**
 * GSAP-based animation engine
 * - Reads configuration from element data attributes when available
 * - Provides a small set of sensible presets; extend as needed
 */

function getDatasetKeyWithSuffix(key, suffix) {
  if (!suffix) return key;
  return `${key}${suffix}`;
}

function getNumberDataset(el, key, fallback, suffix) {
  const preferredKey = getDatasetKeyWithSuffix(key, suffix);
  const preferred = el.dataset[preferredKey];
  const base = el.dataset[key];
  const value = preferred != null ? preferred : base;
  if (value == null) return fallback;
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
}

function getStringDataset(el, key, fallback, suffix) {
  const preferredKey = getDatasetKeyWithSuffix(key, suffix);
  const preferred = el.dataset[preferredKey];
  const base = el.dataset[key];
  const value = preferred != null ? preferred : base;
  return value == null || value === '' ? fallback : value;
}

const presetBuilders = {
  // Fade from below into place
  'fade-in-up': (el, opts = {}) => ({
    targets: el,
    from: { opacity: 0, y: 50 },
    to: { opacity: 1, y: 0 },
    duration: getNumberDataset(el, 'animateDuration', opts.duration ?? 0.8, opts.datasetSuffix), // seconds
    delay: getNumberDataset(el, 'animateDelay', opts.delay ?? 0, opts.datasetSuffix), // seconds
    ease: getStringDataset(el, 'animateEase', opts.ease ?? 'power1.inOut', opts.datasetSuffix),
  }),

  'fade-in-right': (el, opts = {}) => ({
    targets: el,
    from: { opacity: 0, x: 50 },
    to: { opacity: 1, x: 0 },
    duration: getNumberDataset(el, 'animateDuration', opts.duration ?? 0.8, opts.datasetSuffix), // seconds
    delay: getNumberDataset(el, 'animateDelay', opts.delay ?? 0, opts.datasetSuffix), // seconds
    ease: getStringDataset(el, 'animateEase', opts.ease ?? 'power2.inOut', opts.datasetSuffix),
  }),

  'fade-in-left': (el, opts = {}) => ({
    targets: el,
    from: { opacity: 0, x: -50 },
    to: { opacity: 1, x: 0 },
    duration: getNumberDataset(el, 'animateDuration', opts.duration ?? 0.8, opts.datasetSuffix), // seconds
    delay: getNumberDataset(el, 'animateDelay', opts.delay ?? 0, opts.datasetSuffix), // seconds
    ease: getStringDataset(el, 'animateEase', opts.ease ?? 'power2.inOut', opts.datasetSuffix),
  }),

  'fade-in-down': (el, opts = {}) => ({
    targets: el,
    from: { opacity: 0, y: 50 },
    to: { opacity: 1, y: 0 },
    duration: getNumberDataset(el, 'animateDuration', opts.duration ?? 0.8, opts.datasetSuffix), // seconds
    delay: getNumberDataset(el, 'animateDelay', opts.delay ?? 0, opts.datasetSuffix), // seconds
    ease: getStringDataset(el, 'animateEase', opts.ease ?? 'power2.inOut', opts.datasetSuffix),
  }),

  // Simple fade in
  'fade-in': (el, opts = {}) => ({
    targets: el,
    from: { opacity: 0 },
    to: { opacity: 1 },
    duration: getNumberDataset(el, 'animateDuration', opts.duration ?? 0.6, opts.datasetSuffix),
    delay: getNumberDataset(el, 'animateDelay', opts.delay ?? 0, opts.datasetSuffix),
    ease: getStringDataset(el, 'animateEase', opts.ease ?? 'power2.inOut', opts.datasetSuffix),
  }),
};

export const engine = {
  /**
   * Play an animation preset on a given element.
   */
  play(el, presetName, opts = {}) {
    const build = presetBuilders[presetName] || presetBuilders['fade-in'];
    const cfg = build(el, opts);

    // Stagger support for children
    const stagger = getNumberDataset(el, 'animateStagger', null, opts.datasetSuffix);
    const targets = stagger ? Array.from(el.children) : [cfg.targets];

    // Performance hint
    targets.forEach(t => t && t.style && t.style.setProperty('will-change', 'opacity, transform'));

    if (stagger) {
      // The children fade in one by one, so the container itself must not stay hidden by [data-animate].
      gsap.set(el, { opacity: 1 });

      // Use fromTo for consistent initial state
      gsap.fromTo(targets, cfg.from, {
        ...cfg.to,
        duration: cfg.duration,
        delay: cfg.delay,
        ease: cfg.ease,
        stagger: { each: stagger / 1000 },
        onComplete: () => targets.forEach(t => t.style.setProperty('will-change', 'auto')),
      });
      return;
    }

    gsap.fromTo(cfg.targets, cfg.from, {
      ...cfg.to,
      duration: cfg.duration,
      delay: cfg.delay,
      ease: cfg.ease,
      onComplete: () => {
        if (el && el.style) el.style.setProperty('will-change', 'auto');
        if (el.dataset.animate) {
          el.classList.add(`animated`);
        }
      },
    });
  },
};

export default engine;
